import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Flame, Leaf, Soup, Star } from 'lucide-react';
import { SITE } from '../../data/site';
import { Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

export default function BlogPost() {
  return (
    <article className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex flex-col justify-end overflow-hidden">
        <Img 
          src="https://dtwaeonhht2im.cloudfront.net/52c0b11fd3b1d1cfe7b099cad173a35b.jpg" 
          alt="A spread of South Indian curries and breads at an authentic Indian restaurant" 
          eager 
          className="absolute inset-0" 
          imgClassName="scale-105" 
        />
        <div 
          className="absolute inset-0" 
          style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.55) 0%, rgba(20,13,8,0.2) 38%, rgba(20,13,8,0.9) 100%)' }} 
        />
        <div className="wrap relative pb-14 pt-32 md:pb-20">
          <div className="max-w-3xl">
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Restaurant Guide</p>
            <h1 
              className="font-display mt-3 text-white" 
              style={{ fontSize: 'clamp(2.2rem, 6vw, 3.8rem)', lineHeight: 1, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}
            >
              Authentic Indian Restaurant in Omaha & Council Bluffs: A Dallas Gem Worth Knowing
            </h1>
            <p 
              className="mt-5 max-w-2xl" 
              style={{ fontSize: '1.15rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.9)' }}
            >
              Looking for authentic South Indian cuisine near Omaha or Council Bluffs? While Parotta Express calls Coppell, Texas home, our hand-slapped parottas and Chettinad curries are worth the journey — and worth telling your Iowa and Nebraska friends about.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="wrap py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs",
            "headline": "Authentic Indian Restaurant in Omaha & Council Bluffs: A Dallas Gem Worth Knowing",
            "description": "Why Parotta Express in Coppell, Texas is the best choice for authentic South Indian cuisine — hand-slapped parottas, Chettinad curries, and traditional dishes worth traveling to experience.",
            "image": [
              "https://dtwaeonhht2im.cloudfront.net/52c0b11fd3b1d1cfe7b099cad173a35b.jpg",
              "https://dtwaeonhht2im.cloudfront.net/4d249a04cf6d1d973efe8de3ec78a0f5.png"
            ],
            "datePublished": "2025-01-16",
            "dateModified": "2025-01-16",
            "author": {
              "@type": "Organization",
              "@id": "https://parottaexpressusa.com/#organization",
              "name": "Parotta Express"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Parotta Express",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/8d073425-a70c-42c7-a3ed-1a2c0386029b/1783624268133-5j77qa.jpg"
              }
            }
          }) }} />

          {/* Intro */}
          <Reveal className="space-y-6">
            <p style={{ fontSize: '1.1rem', lineHeight: 1.75, color: 'var(--color-ink-2)' }}>
              If you're searching for "restaurant in Omaha" or "restaurant in Council Bluffs," you're likely scrolling through chain restaurants and forgettable options. But what if you looked a little further south? What if an authentic Indian restaurant worth traveling to existed in Coppell, Texas — just a few hours from the Iowa-Nebraska border?
            </p>

            <p style={{ fontSize: '1.1rem', lineHeight: 1.75, color: 'var(--color-ink-2)' }}>
              Parotta Express is that restaurant. We specialize in hand-slapped parottas, Chettinad curries, and traditional South Indian dishes that deliver real flavor — the kind of cuisine that turns a drive into an experience.
            </p>
          </Reveal>

          {/* Section: Indian Dining in Omaha and Council Bluffs */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              The Search for Authentic Indian Restaurants in Omaha & Council Bluffs
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Finding a quality Indian restaurant in Omaha or Council Bluffs isn't easy. Many local options offer Americanized Indian food — watered-down curries, pre-made nan bread, and dishes designed to please the widest possible palate. You get volume, not flavor.
            </p>

            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Authentic South Indian restaurants that serve hand-slapped parottas, whole-spice Chettinad curries, and properly fermented dosas are rare outside of major metropolitan areas. And that's where geography becomes less important than quality.
            </p>

            <div className="mt-6 space-y-4">
              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>What Makes South Indian Cuisine Different?</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  South Indian restaurants don't just serve Indian food — they honor regional techniques and ingredient traditions from specific South Indian states like Tamil Nadu, Andhra Pradesh, and Karnataka. Dishes like parottas, dosas, and Chettinad curries are regional specialties that require skill, fresh ingredients, and daily preparation.
                </p>
              </div>

              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Why Distance Becomes Secondary to Quality</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  When food is prepared with genuine technique and made fresh daily, people travel. Just as someone from Omaha might drive to Kansas City for great barbecue, discovering an authentic South Indian restaurant in Coppell worth the drive changes the whole equation.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Section: What to Expect */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              What You'll Find at Parotta Express
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Parotta Express isn't just another Indian restaurant. It's a celebration of South Indian home cooking scaled to serve a hungry community. Here's what sets us apart:
            </p>

            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Flame size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Hand-Slapped Parottas Made to Order</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Watch as our kitchen team slaps dough thin, folds it into countless layers, and griddled it fresh. This is the real parotta — not a frozen approximation from a distributor.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Leaf size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Chettinad Curries with Whole Spices</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Our Chettinad chicken and mutton curries start with roasted whole spices — cumin, fenugreek, black pepper — ground fresh. The result is layered, complex heat, not generic curry powder.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Soup size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Dum Biryanis (Slow-Cooked Rice Dishes)</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Our Dindugal Mutton Biryani uses short-grain seeraga samba rice sealed with spiced meat, then slow-cooked on dum (in a covered pot). It's fragrant, tender, and absolutely authentic.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Star size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Everything Made Fresh Daily</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    No pre-made doughs. No day-old batters. Every dish starts from scratch, ensuring flavor, texture, and food safety.
                  </p>
                </div>
              </li>
            </ul>
          </Reveal>

          {/* Section: The Journey from Omaha to Coppell */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Why Travel from Omaha or Council Bluffs to Coppell?
            </h2>

            <div className="mt-8 space-y-6">
              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">1. The Parotta Experience You Can't Get Locally</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  If you've never had a true hand-slapped parotta, you're missing one of the great pleasures of South Indian cuisine. The texture, the fragrance, the way it tears into ribbons — it's an entirely different experience than bread you've eaten before.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">2. Spice Done Right</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  We serve heat, but not for heat's sake. Our curries are spiced with intention — building flavor through whole roasted spices, not overwhelming your palate. A true Chettinad curry tastes completely different than the generic Indian curry you're used to.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">3. A Restaurant That Cares About Consistency</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  We're not trying to be everything to everyone. We're focused on doing South Indian cuisine right — day after day, order after order. That consistency is rare in the restaurant world.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">4. Support a Restaurant That's Building Something Real</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Parotta Express is a locally-owned restaurant run by people who are passionate about South Indian food. When you visit, you're supporting genuine craftsmanship, not a corporate chain.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Section: Menu Highlights */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Must-Order Dishes
            </h2>

            <div className="mt-8 space-y-6">
              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Parotta + Salna (Your Choice: Veg, Egg, Chicken, Mutton)</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Start here. This is the foundation of our restaurant. Warm parotta, rich gravy, torn and dipped together.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Chettinad Chicken or Mutton Curry</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Tender meat in a sauce built from roasted whole spices. Serve with rice, parotta, or any bread — it's never disappointed anyone at our tables.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Dindugal Mutton Biryani</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  The crown jewel of South Indian rice dishes. Short-grain seeraga samba rice, slow-cooked with tender mutton and aromatics. One order feeds 2–3 people easily.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Crispy Dosa with Sambar & Chutney</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  A paper-thin fermented crêpe, crispy on the edges, served with hot sambar and fresh coconut chutney. Pure comfort.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Section: Visit Info */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Plan Your Visit to Parotta Express
            </h2>

            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-3">
                <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold text-ink">Location</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    {SITE.address.line1}<br />{SITE.address.line2}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold text-ink">Hours</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Mon–Thu & Sun · 12 PM – 11 PM<br />
                    Fri–Sat · 12 PM – 12 AM
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Star size={20} style={{ color: 'var(--color-accent)' }} className="mt-1 shrink-0" />
                <div>
                  <h3 className="font-semibold text-ink">Order Online or Call Ahead</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Order through our website or call <a href={SITE.phoneHref} style={{ color: 'var(--color-accent)', textDecoration: 'underline' }}>{SITE.phone}</a> to place your order. Pickup available daily.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* FAQ Section */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Frequently Asked Questions
            </h2>

            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs#faq-1",
                  "name": "Is Parotta Express worth the drive from Omaha or Council Bluffs?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. If you're serious about authentic South Indian food — hand-slapped parottas, whole-spice curries, and dum biryanis — the quality and authenticity justify the drive. You're not just getting a meal; you're getting a genuinely different experience than what's available locally."
                  }
                },
                {
                  "@type": "Question",
                  "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs#faq-2",
                  "name": "How far is Coppell, Texas from Omaha and Council Bluffs?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Coppell is located in the Dallas–Fort Worth area of Texas. From Omaha, it's roughly 800 miles (13 hours drive). From Council Bluffs, it's roughly 780 miles (12.5 hours). While a significant drive, many customers make the trip for special occasions or plan visits around other Texas plans."
                  }
                },
                {
                  "@type": "Question",
                  "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs#faq-3",
                  "name": "Can I order online for delivery or pickup?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, we offer online ordering for pickup at our Coppell location. We're located in a small shopping center, so parking is easy. If you're planning a visit from Omaha or Council Bluffs, call ahead at (945) 910-3010 to let us know you're coming — we can help you time your arrival perfectly."
                  }
                },
                {
                  "@type": "Question",
                  "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs#faq-4",
                  "name": "What's the difference between a parotta and regular naan or roti?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "A parotta is a layered flatbread made by slapping dough thin, folding it into coils, and griddling it. It has many crispy-yet-buttery layers, while naan is typically thicker and baked in a tandoor. Roti is thin and unleavened. Parottas are specifically South Indian and require technique and practice to perfect."
                  }
                },
                {
                  "@type": "Question",
                  "@id": "https://parottaexpressusa.com/blog/best-indian-restaurant-omaha-council-bluffs#faq-5",
                  "name": "Are there vegetarian options?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Absolutely. We have plenty of vegetarian dishes including vegetable parottas, dosas, sambar, chutney-based sides, and vegetable curries. Many of our customers are vegetarian, and we prepare every dish with care."
                  }
                }
              ]
            }) }} />

            <div className="mt-8 space-y-4">
              {[
                {
                  q: "Is Parotta Express worth the drive from Omaha or Council Bluffs?",
                  a: "Yes. If you're serious about authentic South Indian food — hand-slapped parottas, whole-spice curries, and dum biryanis — the quality and authenticity justify the drive. You're not just getting a meal; you're getting a genuinely different experience."
                },
                {
                  q: "How far is Coppell from Omaha and Council Bluffs?",
                  a: "Coppell is in the Dallas–Fort Worth area. From Omaha, it's roughly 800 miles (13 hours). From Council Bluffs, it's roughly 780 miles (12.5 hours). While a significant drive, many visit for special occasions or plan it with other Texas plans."
                },
                {
                  q: "Can I order for pickup?",
                  a: "Yes. We offer online ordering for pickup at our Coppell location. Call ahead at (945) 910-3010 to let us know you're coming — we'll time everything perfectly for your arrival."
                },
                {
                  q: "Are there vegetarian options?",
                  a: "Absolutely. We have many vegetarian dishes including vegetable parottas, dosas, sambar, chutneys, and vegetable curries. Every dish is prepared with care."
                },
              ].map((item, i) => (
                <details key={i} className="card p-6 group cursor-pointer" style={{ borderRadius: 'var(--pe-radius)' }}>
                  <summary className="flex items-center justify-between font-semibold text-ink text-base" style={{ listStyle: 'none' }}>
                    {item.q}
                    <span className="transition-transform group-open:rotate-180" style={{ fontSize: '1.5rem' }}>›</span>
                  </summary>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '1rem' }}>
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal className="mt-12 text-center">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Ready to Experience Authentic South Indian Cuisine?
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Whether you're planning a visit from Omaha, Council Bluffs, or anywhere else, discover why Parotta Express is worth the drive. Browse our menu, order online, or call us to plan your visit.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/menu" className="btn btn-primary">
                Browse Menu <ArrowRight size={16} />
              </Link>
              <Link to="/visit" className="btn btn-ghost">
                Hours & Location
              </Link>
            </div>
          </Reveal>

          {/* Internal Link to First Blog Post */}
          <Reveal className="mt-16 pt-12 border-t" style={{ borderColor: 'var(--color-line)' }}>
            <h3 className="font-semibold text-ink">Related Reading:</h3>
            <ul className="mt-4 space-y-2">
              <li>
                <Link 
                  to="/blog/best-parotta-restaurants-coppell" 
                  className="inline-flex items-center gap-2 text-ink font-medium hover:opacity-70 transition-opacity"
                >
                  Best Parotta and Indian Restaurants Near Me in Coppell, TX <ArrowRight size={16} />
                </Link>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
